import { useParams, Navigate, Link } from "react-router-dom";
import { Check, Hammer, Wrench, ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { PageHero, SectionHeading, Reveal, CTABand, SERVICE_ICONS, usePageMeta } from "@/components/kit";
import { SERVICES, SLOGAN } from "@/lib/site";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const svc = SERVICES.find((s) => s.slug === slug);
  usePageMeta(
    svc ? `${svc.name} in Christiana, TN | Redeemer Solutions LLC` : "Services | Redeemer Solutions LLC",
    svc ? svc.short : undefined
  );
  if (!svc) return <Navigate to="/services" replace />;
  const Icon = SERVICE_ICONS[svc.icon];
  const others = SERVICES.filter((s) => s.slug !== slug);

  return (
    <div data-testid={`service-page-${svc.slug}`}>
      <PageHero
        testid={`service-hero-${svc.slug}`}
        eyebrow={`Services — ${svc.shortName}`}
        lines={[svc.heroLines[0], { t: svc.heroLines[1], gold: true }]}
        sub={svc.tagline}
        image={svc.image}
        imageAlt={svc.imageAlt}
      />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-7">
          <SectionHeading eyebrow="Overview" title={svc.name} testid={`service-overview-${svc.slug}`} />
          <Reveal className="mt-7">
            <p className="text-zinc-300 text-base leading-relaxed">{svc.intro}</p>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-12">
            <Reveal delay={0.05}>
              <div className="bg-[#121216] border border-gold/15 rounded-sm p-7 h-full">
                <div className="flex items-center gap-3">
                  <Hammer size={18} className="text-gold" />
                  <h3 className="font-display text-base font-semibold text-gold-champagne">Service & Maintenance</h3>
                </div>
                <ul className="mt-5 space-y-3">
                  {svc.service.map((item) => (
                    <li key={item} className="flex gap-3 text-sm text-zinc-300 leading-relaxed">
                      <Check size={15} className="text-gold mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="bg-[#121216] border border-gold/15 rounded-sm p-7 h-full">
                <div className="flex items-center gap-3">
                  <Wrench size={18} className="text-gold" />
                  <h3 className="font-display text-base font-semibold text-gold-champagne">Repair & Restoration</h3>
                </div>
                <ul className="mt-5 space-y-3">
                  {svc.repair.map((item) => (
                    <li key={item} className="flex gap-3 text-sm text-zinc-300 leading-relaxed">
                      <Check size={15} className="text-gold mt-0.5 shrink-0" /> {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="lg:col-span-5">
          <Reveal delay={0.1}>
            <div className="bg-[#0D0D10] border border-gold/20 rounded-sm p-8 sm:p-9 sticky top-28">
              <div className="flex items-center gap-3">
                <Icon size={20} className="text-gold" />
                <p className="font-mono-label text-xs uppercase tracking-[0.26em] text-gold">Key Benefits</p>
              </div>
              <ul className="mt-7 space-y-4">
                {svc.benefits.map((b) => (
                  <li key={b} className="flex gap-3 text-sm text-zinc-300 leading-relaxed">
                    <Check size={15} className="text-gold mt-0.5 shrink-0" /> {b}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-gold/15 bg-[#0D0D10]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <SectionHeading
            eyebrow="Common Questions"
            title={`${svc.shortName} — answered honestly.`}
            align="center"
            testid={`service-faq-heading-${svc.slug}`}
          />
          <Reveal className="mt-12">
            <Accordion type="single" collapsible data-testid={`service-faq-${svc.slug}`}>
              {svc.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-gold/15">
                  <AccordionTrigger data-testid={`faq-trigger-${svc.slug}-${i}`} className="text-left text-[#F4EFE6] hover:text-gold-champagne font-medium text-base py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-zinc-400 text-sm leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20">
        <SectionHeading eyebrow="Keep Exploring" title="Related services" testid={`service-related-${svc.slug}`} />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10">
          {others.map((o, i) => {
            const OIcon = SERVICE_ICONS[o.icon];
            return (
              <Reveal key={o.slug} delay={i * 0.06}>
                <Link
                  to={`/services/${o.slug}`}
                  data-testid={`related-service-${o.slug}`}
                  className="group block bg-[#121216] border border-gold/15 rounded-sm p-6 gold-glow-card h-full"
                >
                  <OIcon size={20} className="text-gold" />
                  <h3 className="font-display text-base font-semibold text-[#F4EFE6] mt-4 leading-snug">{o.name}</h3>
                  <span className="inline-flex items-center gap-2 text-gold text-[11px] font-mono-label uppercase tracking-[0.2em] mt-4 group-hover:gap-3 transition-all">
                    View <ArrowRight size={12} />
                  </span>
                </Link>
              </Reveal>
            );
          })}
        </div>
      </section>

      <CTABand title={SLOGAN} sub={`Ready to get your ${svc.shortName.toLowerCase()} working right? Fast dispatch across Middle Tennessee.`} />
    </div>
  );
}
